import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'

const GlobalShareModal = () => {
  const [showModal, setShowModal] = useState(false)
  const [firmantesConTokens, setFirmantesConTokens] = useState([])
  const [documentoNombre, setDocumentoNombre] = useState('')
  const [timeRemaining, setTimeRemaining] = useState(30)
  
  const timeoutRef = useRef(null)
  const intervalRef = useRef(null)
  const isModalOpenRef = useRef(false)

  // Función para cerrar el modal
  const cerrarModal = React.useCallback(() => {
    console.log('[GlobalShareModal] 🚪 Cerrando modal')
    
    // Limpiar timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    isModalOpenRef.current = false
    setShowModal(false)
    setFirmantesConTokens([])
    setDocumentoNombre('')
    setTimeRemaining(30)
    
    // Limpiar sessionStorage
    try {
      sessionStorage.removeItem('pdfEditor_showShareModal')
      sessionStorage.removeItem('pdfEditor_firmantesConTokens')
      sessionStorage.removeItem('pdfEditor_documentoNombre')
      sessionStorage.removeItem('pdfEditor_modalStartTime')
    } catch (e) {
      console.error('[GlobalShareModal] Error:', e)
    }
    
    // Navegar al dashboard
    setTimeout(() => {
      window.location.href = '/dashboard'
    }, 100)
  }, [])

  // Función para activar el modal
  const activateModal = React.useCallback((firmantes, nombre) => {
    if (isModalOpenRef.current) {
      console.log('[GlobalShareModal] ⚠️ Modal ya está abierto')
      return
    }
    
    console.log('[GlobalShareModal] ✅ ACTIVANDO MODAL con', firmantes.length, 'firmantes')
    isModalOpenRef.current = true
    setFirmantesConTokens(firmantes)
    setDocumentoNombre(nombre || 'Documento')
    setShowModal(true)
    setTimeRemaining(30)
    
    // Iniciar cuenta regresiva
    let secondsLeft = 30
    intervalRef.current = setInterval(() => {
      secondsLeft--
      setTimeRemaining(secondsLeft)
      
      if (secondsLeft <= 0) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
        cerrarModal()
      }
    }, 1000)
    
    // Timeout de seguridad
    timeoutRef.current = setTimeout(() => {
      console.log('[GlobalShareModal] ⏰ Timeout de 30 segundos completado')
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      cerrarModal()
    }, 30000)
  }, [cerrarModal])

  // Verificar sessionStorage y eventos
  useEffect(() => {
    const checkAndActivate = () => {
      if (isModalOpenRef.current) return
      
      const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
      const savedFirmantes = sessionStorage.getItem('pdfEditor_firmantesConTokens')
      const savedNombre = sessionStorage.getItem('pdfEditor_documentoNombre')
      
      if (savedShowModal === 'true' && savedFirmantes) {
        try {
          const firmantes = JSON.parse(savedFirmantes)
          if (firmantes && firmantes.length > 0) {
            activateModal(firmantes, savedNombre || 'Documento')
          }
        } catch (e) {
          console.error('[GlobalShareModal] Error:', e)
        }
      }
    }

    // Verificar inmediatamente
    checkAndActivate()

    // Escuchar evento personalizado
    const handleUpdate = () => {
      console.log('[GlobalShareModal] 📢 Evento recibido')
      checkAndActivate()
    }
    window.addEventListener('pdfEditor_shareModalUpdated', handleUpdate)

    // Verificar periódicamente
    const interval = setInterval(() => {
      if (!isModalOpenRef.current) {
        checkAndActivate()
      }
    }, 200)

    return () => {
      window.removeEventListener('pdfEditor_shareModalUpdated', handleUpdate)
      clearInterval(interval)
    }
  }, [activateModal])

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const getFirmarUrl = (tokenFirma) => {
    return `${window.location.origin}/registrar/${tokenFirma}`
  }

  const copiarEnlace = (tokenFirma) => {
    const link = getFirmarUrl(tokenFirma)
    navigator.clipboard.writeText(link)
    toast.success('Enlace copiado al portapapeles')
  }

  const compartirPorWhatsApp = (tokenFirma, nombreFirmante, tituloDocumento) => {
    const linkFirma = getFirmarUrl(tokenFirma)
    const mensaje = `Hola ${nombreFirmante},\n\nTe invito a firmar el documento: ${tituloDocumento}\n\nPara firmar, haz clic en el siguiente enlace:\n${linkFirma}\n\nSaludos`
    const urlWhatsApp = `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    window.open(urlWhatsApp, '_blank')
  }

  // Función para obtener la inicial del nombre
  const getInitial = (nombre) => {
    if (!nombre) return '?'
    return nombre.charAt(0).toUpperCase()
  }

  // Función para abrir el enlace
  const abrirEnlace = (tokenFirma) => {
    const link = getFirmarUrl(tokenFirma)
    // Cerrar el modal primero
    cerrarModal()
    // Luego navegar al enlace en la misma ventana
    setTimeout(() => {
      window.location.href = link
    }, 100)
  }

  if (!showModal) return null

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] backdrop-blur-sm"
      style={{ zIndex: 99999 }}
      onClick={cerrarModal}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header azul con gradiente */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 relative">
          <button
            onClick={cerrarModal}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white">
              Documento Enviado Exitosamente
            </h2>
          </div>
          
          <p className="text-white text-sm mb-2">
            Comparte los enlaces de firma con los firmantes
          </p>
          
          <p className="text-white font-semibold">
            Documento: {documentoNombre}
          </p>
        </div>

        {/* Contenido blanco */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            {firmantesConTokens.map((firmante, index) => {
              const linkFirma = getFirmarUrl(firmante.token_firma)
              const nombreCompleto = `${firmante.nombre || ''} ${firmante.apellido || ''}`.trim()
              
              return (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                  {/* Información del firmante */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">
                        {getInitial(firmante.nombre || firmante.email)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">
                        {nombreCompleto || firmante.email}
                      </p>
                      <p className="text-sm text-gray-500">
                        {firmante.email}
                      </p>
                    </div>
                  </div>

                  {/* Enlace de firma */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      ENLACE DE FIRMA
                    </p>
                    <div 
                      onClick={() => abrirEnlace(firmante.token_firma)}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer hover:bg-blue-100 transition-colors group"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        <span className="text-blue-600 text-sm break-all group-hover:underline">
                          {linkFirma}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => copiarEnlace(firmante.token_firma)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copiar Enlace
                    </button>
                    <button
                      onClick={() => compartirPorWhatsApp(firmante.token_firma, nombreCompleto, documentoNombre)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Compartir por WhatsApp
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer con botón */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={cerrarModal}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default GlobalShareModal
